import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps, GridWrapper } from "@insite/mobius/GridContainer";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import NavigationGridItem from "@midland-industries/Components/NavigationGridItem";
import { CustomFieldDefinition } from "@midland-industries/FieldDefinitions/CustomFieldDefinition";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    numberOfItemsToDisplay = "numberOfItemsToDisplay",
    maximumNumberOfItemsPerRow = "maximumNumberOfItemsPerRow",
    blogListField = "blogListField",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfItemsToDisplay]: number;
        [fields.maximumNumberOfItemsPerRow]: string;
        [fields.blogListField]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    let blogListField = getCurrentPage(state).nodeId;
    if (ownProps.fields.blogListField.length > 0) {
        blogListField = ownProps.fields.blogListField;
    }
    const pages = getDataView(state.data.pages, {
        parentNodeId: blogListField,
    });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({
                ...links,
                [page.nodeId]: getPageLinkByNodeId(state, page.nodeId),
            }),
            {},
        );
    }
    return {
        numberOfItemsToDisplay: parseInt(ownProps.fields.numberOfItemsToDisplay || "0", 10),
        parentNodeId: blogListField,
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[blogListField] ?? {
            parentNodeId: blogListField,
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NavigationGridViewStyles {
    container?: GridContainerProps;
}

export const navigationGridViewStyles: NavigationGridViewStyles = {
    container: {
        gap: 20,
    },
};

const styles = navigationGridViewStyles;

class RecentBlogs extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        if (this.props.pages && !this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
    }

    componentDidUpdate() {
        if (this.props.pages && !this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { numberOfItemsToDisplay, maximumNumberOfItemsPerRow },
            parameter,
        } = this.props;

        if (!pages) {
            return null;
        }

        const maxItemsPerRow = parseInt(maximumNumberOfItemsPerRow, 10);

        const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"]).reverse();

        const reducedSortedAndPagedPages = sortedAndPagedPages.slice(0, numberOfItemsToDisplay);

        return (
            <GridWrapper>
                <GridContainer {...styles.container} data-test-selector="CmsChildPage_container">
                    {reducedSortedAndPagedPages.map((page: PageProps) => {
                        const link = links[page.nodeId];
                        if (link) {
                            return (
                                <NavigationGridItem
                                    key={page.id}
                                    pageUrl={link.url}
                                    imageUrl={page.fields["openGraphImage"]}
                                    pageId={page.id}
                                    imageAltText={page.fields["title"]}
                                    titleText={page.fields["title"]}
                                    maxItemsPerRow={maxItemsPerRow}
                                />
                            );
                        }
                        return null;
                    })}
                </GridContainer>
            </GridWrapper>
        );
    }
}

// we need to pass our CustomFieldDefinition so that we can use the CustomTemplate template below
export const definition: WidgetDefinition<CustomFieldDefinition> = {
    group: "Custom List" as any, // Extend the standard groups with `as any`.
    icon: "list-ul",
    fieldDefinitions: [
        {
            name: fields.maximumNumberOfItemsPerRow,
            displayName: "Maximum items per row",
            editorTemplate: "DropDownField",
            defaultValue: "6",
            options: [
                { displayName: "1", value: "1" },
                { displayName: "2", value: "2" },
                { displayName: "3", value: "3" },
                { displayName: "4", value: "4" },
                { displayName: "5", value: "5" },
                { displayName: "6", value: "6" },
            ],
            fieldType: "General",
            tooltip:
                "Up to 6 links can display per row. This number should be adjusted based on the number of links you choose to display. For example, if you display 10 pages you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
        },
        {
            name: fields.numberOfItemsToDisplay,
            displayName: "Maximum Number of items to display",
            editorTemplate: "IntegerField",
            defaultValue: 5,
            fieldType: "General",
            min: 1,
            tooltip: "The maximum number of Recent News Pages to display",
        },
        {
            name: fields.blogListField,
            editorTemplate: "NewsListField",
            defaultValue: "",
            fieldType: "General",
            hideEmptyOption: false,
            isRequired: true,
            tooltip: "Selected NewsList Page",
        },
    ],
};

// because we have a CustomFieldDefinition, we need to include that type here
const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(RecentBlogs)),
    definition,
};

export default widgetModule;
